import {
  ANNOUNCEMENT_CRITERIA_VALUES,
  type Announcement,
  type AnnouncementCriteria,
} from '@clockwise/clients/announcements';
import { ApiError, isValidIsoDate } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { ANNOUNCEMENT_CRITERIA_LABELS } from '@/components/custom/announcement-criteria-badge';
import { RequirePermission } from '@/components/custom/require-permission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { announcementsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Add / edit an announcement banner, and (when editing) publish/unpublish it.
 * Publishing is a deliberately SEPARATE mutation from Save — a draft never
 * goes live site-wide just because someone clicked Save — see
 * docs/announcements.md.
 *
 * Start/end are collected as a local date + a half-hour time slot (no
 * existing shared time picker in this app, so it's a small local constant
 * here rather than a new shared component — see the domain-builder skill's
 * G10 guidance) and combined into one UTC instant at submit time, per the
 * app's timezone-handling convention: convert the admin's local input to UTC
 * at the boundary, not earlier.
 */

const HALF_HOUR_OPTIONS: readonly { value: string; label: string }[] = Array.from(
  { length: 48 },
  (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? 0 : 30;
    const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    const label = format(new Date(2000, 0, 1, hour, minute), 'h:mm a');
    return { value, label };
  },
);

const AnnouncementFormSchema = z
  .object({
    criteria: z.enum(ANNOUNCEMENT_CRITERIA_VALUES),
    heading: z.string().trim().min(1, 'Required').max(200, 'Up to 200 characters'),
    subheading: z.string().max(300, 'Up to 300 characters'),
    explanation: z.string().max(10000, 'Up to 10,000 characters'),
    startDate: z.string().min(1, 'Pick a date').refine(isValidIsoDate, 'Pick a valid date'),
    startTime: z.string().min(1, 'Pick a time'),
    // '' means "no end" — kept as strings (not nullable) because react-hook-form
    // inputs always have a defined value; emptiness is the "none" sentinel.
    endDate: z.string(),
    endTime: z.string(),
  })
  .refine((v) => !v.endDate || isValidIsoDate(v.endDate), {
    message: 'Pick a valid date',
    path: ['endDate'],
  })
  .refine((v) => !v.endDate || v.endTime.length > 0, {
    message: 'Pick an end time, or clear the end date',
    path: ['endTime'],
  });
type AnnouncementFormValues = z.infer<typeof AnnouncementFormSchema>;

const blankValues: AnnouncementFormValues = {
  criteria: 'information',
  heading: '',
  subheading: '',
  explanation: '',
  startDate: '',
  startTime: '09:00',
  endDate: '',
  endTime: '',
};

interface AnnouncementFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The row being edited, or `null` to add a new announcement. */
  announcement: Announcement | null;
}

export function AnnouncementFormSheet({
  open,
  onOpenChange,
  announcement,
}: AnnouncementFormSheetProps) {
  const isEdit = announcement !== null;
  const isDeleted = announcement !== null && !announcement.active;
  const queryClient = useQueryClient();

  const form = useForm<AnnouncementFormValues>({
    resolver: zodResolver(AnnouncementFormSchema),
    defaultValues: blankValues,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(announcement ? toFormValues(announcement) : blankValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, announcement?.id]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const saveMutation = useMutation({
    mutationFn: (values: AnnouncementFormValues) => {
      const body = toRequestBody(values);
      return isEdit && announcement
        ? announcementsApi.patch(announcement.id, body)
        : announcementsApi.create(body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Announcement updated' : 'Announcement saved as draft');
      invalidate();
      onOpenChange(false);
    },
    onError: (err) => applyFieldErrors(form, err),
  });

  // `values` is set when the form had unsaved edits at click time — they ride
  // along in the same PATCH so Publish never goes live with stale content.
  const publishMutation = useMutation({
    mutationFn: ({ isPublished, values }: PublishVariables) => {
      if (!announcement) throw new Error('No announcement selected');
      return announcementsApi.patch(announcement.id, {
        ...(values ? toRequestBody(values) : {}),
        isPublished,
      });
    },
    onSuccess: (_, { isPublished, values }) => {
      const action = isPublished ? 'published' : 'unpublished';
      toast.success(values ? `Changes saved and announcement ${action}` : `Announcement ${action}`);
      invalidate();
      onOpenChange(false);
    },
    onError: (err, { values }) => {
      if (values) {
        applyFieldErrors(form, err);
        return;
      }
      toast.error(err instanceof ApiError ? err.message : 'Could not update publish state');
    },
  });

  const busy = saveMutation.isPending || publishMutation.isPending;
  const onSubmit = form.handleSubmit((v) => saveMutation.mutate(v));
  const onTogglePublish = (isPublished: boolean) => {
    if (!form.formState.isDirty) {
      publishMutation.mutate({ isPublished });
      return;
    }
    // Validate the pending edits first; an invalid form surfaces its field
    // errors and nothing is published.
    void form.handleSubmit((values) => publishMutation.mutate({ isPublished, values }))();
  };

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: busy,
    onOpenChange,
  });

  const endDate = form.watch('endDate');
  const errors = form.formState.errors;

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit announcement' : 'Add announcement'}</SheetTitle>
            <SheetDescription>
              {isDeleted
                ? 'This announcement is deleted. Restore it from the list to publish or unpublish it.'
                : 'Saving always keeps this as a draft or its current publish state — publishing site-wide is a separate step below.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-4">
              <div className="space-y-1.5">
                <Label htmlFor="announcement-criteria">Criteria</Label>
                <Select
                  value={form.watch('criteria')}
                  onValueChange={(v) =>
                    form.setValue('criteria', v as AnnouncementCriteria, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                >
                  <SelectTrigger id="announcement-criteria" disabled={busy}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ANNOUNCEMENT_CRITERIA_VALUES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {ANNOUNCEMENT_CRITERIA_LABELS[c]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="announcement-heading">Heading</Label>
                <Input
                  id="announcement-heading"
                  placeholder="Scheduled maintenance this weekend"
                  disabled={busy}
                  aria-invalid={Boolean(errors.heading)}
                  {...form.register('heading')}
                />
                {errors.heading ? (
                  <p className="text-xs text-destructive">{errors.heading.message}</p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="announcement-subheading">Subheading</Label>
                <Input
                  id="announcement-subheading"
                  placeholder="Optional — a short one-line detail"
                  disabled={busy}
                  aria-invalid={Boolean(errors.subheading)}
                  {...form.register('subheading')}
                />
                {errors.subheading ? (
                  <p className="text-xs text-destructive">{errors.subheading.message}</p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="announcement-explanation">Explanation</Label>
                <Textarea
                  id="announcement-explanation"
                  rows={6}
                  placeholder="Optional — markdown shown in the banner's “Learn more” panel"
                  disabled={busy}
                  aria-invalid={Boolean(errors.explanation)}
                  {...form.register('explanation')}
                />
                {errors.explanation ? (
                  <p className="text-xs text-destructive">{errors.explanation.message}</p>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="announcement-start-date">Starts</Label>
                  <DatePicker
                    id="announcement-start-date"
                    value={form.watch('startDate')}
                    onChange={(v) =>
                      form.setValue('startDate', v, { shouldValidate: true, shouldDirty: true })
                    }
                    disabled={busy}
                    ariaInvalid={Boolean(errors.startDate)}
                  />
                  {errors.startDate ? (
                    <p className="text-xs text-destructive">{errors.startDate.message}</p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="announcement-start-time">Start time</Label>
                  <Select
                    value={form.watch('startTime')}
                    onValueChange={(v) =>
                      form.setValue('startTime', v, { shouldValidate: true, shouldDirty: true })
                    }
                  >
                    <SelectTrigger id="announcement-start-time" disabled={busy}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HALF_HOUR_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="announcement-end-date">Ends</Label>
                  <DatePicker
                    id="announcement-end-date"
                    value={endDate}
                    onChange={(v) => {
                      form.setValue('endDate', v, { shouldValidate: true, shouldDirty: true });
                      // A freshly-picked end date needs SOME end time, or the
                      // refine above blocks submit with a confusing "pick an
                      // end time" error on a field the user never touched.
                      if (v && !form.getValues('endTime')) {
                        form.setValue('endTime', '00:00', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      } else if (!v) {
                        form.setValue('endTime', '', { shouldValidate: true, shouldDirty: true });
                      }
                    }}
                    clearable
                    placeholder="No end date"
                    disabled={busy}
                    ariaInvalid={Boolean(errors.endDate)}
                  />
                  {errors.endDate ? (
                    <p className="text-xs text-destructive">{errors.endDate.message}</p>
                  ) : !endDate ? (
                    <p className="text-xs text-muted-foreground">Runs until unpublished</p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="announcement-end-time">End time</Label>
                  <Select
                    value={form.watch('endTime')}
                    onValueChange={(v) =>
                      form.setValue('endTime', v, { shouldValidate: true, shouldDirty: true })
                    }
                    disabled={!endDate || busy}
                  >
                    <SelectTrigger id="announcement-end-time" disabled={!endDate || busy}>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {HALF_HOUR_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.endTime ? (
                    <p className="text-xs text-destructive">{errors.endTime.message}</p>
                  ) : null}
                </div>
              </div>
              {endDate ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  onClick={() => {
                    form.setValue('endDate', '', { shouldDirty: true });
                    form.setValue('endTime', '', { shouldDirty: true });
                  }}
                >
                  Clear end date
                </button>
              ) : null}
            </div>

            <SheetFooter className="flex-row justify-end gap-2 border-t">
              {/* No Publish/Unpublish on a deleted row: it can't go live, and the
                  API refuses the change — restore it first. */}
              {isEdit && announcement && !isDeleted ? (
                <RequirePermission permission="announcement.edit">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => onTogglePublish(!announcement.isPublished)}
                  >
                    {announcement.isPublished
                      ? form.formState.isDirty
                        ? 'Save & unpublish'
                        : 'Unpublish'
                      : form.formState.isDirty
                        ? 'Save & publish'
                        : 'Publish'}
                  </Button>
                </RequirePermission>
              ) : null}
              <div className="flex flex-1 justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => requestOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={busy}>
                  {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save' : 'Save as draft'}
                </Button>
              </div>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

interface PublishVariables {
  isPublished: boolean;
  values?: AnnouncementFormValues;
}

function toRequestBody(values: AnnouncementFormValues) {
  return {
    criteria: values.criteria,
    heading: values.heading.trim(),
    subheading: values.subheading.trim() || null,
    explanation: values.explanation.trim() || null,
    startAt: combineLocalDateTime(values.startDate, values.startTime).toISOString(),
    endAt: values.endDate
      ? combineLocalDateTime(values.endDate, values.endTime).toISOString()
      : null,
  };
}

/** Admin's local date + half-hour time → the instant that represents, in the
 *  browser's own timezone (matches how the picker/select collected it). */
function combineLocalDateTime(date: string, time: string): Date {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  const [hour = 0, minute = 0] = time.split(':').map(Number);
  return new Date(year, month - 1, day, hour, minute, 0, 0);
}

function toFormValues(a: Announcement): AnnouncementFormValues {
  const start = new Date(a.startAt);
  const end = a.endAt ? new Date(a.endAt) : null;
  return {
    criteria: a.criteria,
    heading: a.heading,
    subheading: a.subheading ?? '',
    explanation: a.explanation ?? '',
    startDate: format(start, 'yyyy-MM-dd'),
    startTime: format(start, 'HH:mm'),
    endDate: end ? format(end, 'yyyy-MM-dd') : '',
    endTime: end ? format(end, 'HH:mm') : '',
  };
}

/** Route the server's field errors to the matching inputs (G12). Server paths
 *  (`startAt`/`endAt`) map onto the date half of the matching form pair —
 *  that's the field closest to what the message is actually about. */
function applyFieldErrors(form: UseFormReturn<AnnouncementFormValues>, err: unknown) {
  const fields = err instanceof ApiError ? err.fields : undefined;
  let matched = false;
  for (const f of fields ?? []) {
    const path = f.path === 'startAt' ? 'startDate' : f.path === 'endAt' ? 'endDate' : f.path;
    if (
      path === 'criteria' ||
      path === 'heading' ||
      path === 'subheading' ||
      path === 'explanation' ||
      path === 'startDate' ||
      path === 'endDate'
    ) {
      form.setError(path, { message: f.message });
      matched = true;
    }
  }
  if (!matched) {
    toast.error(err instanceof ApiError ? err.message : 'Could not save this announcement');
  }
}
